import React, { useRef, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { SelectOptions, BoxShadow } from './Options.styles';
import { OptionsProps } from './types';
import { getBoxShadow } from './utils/getBoxShadow';
import { Option } from './components/Option';

const Options = ({
  selectionIndex,
  options,
  selectSize = 'medium',
  onSelect,
  borderVariant = 'primary',
  selectVariant = 'primary',
  maxHeight = 120,
  maxWidth,
  alignment,
}: OptionsProps) => {
  const [inViewRefTop, inViewTop] = useInView({ initialInView: true });
  const [inViewRefBottom, inViewBottom] = useInView({ initialInView: true });
  const optionsRef = useRef<HTMLDivElement>();
  const [optionsHeight, setOptionsHeight] = useState(null);

  const debounceRef = useRef(null);
  const activeItemRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (activeItemRef.current) {
        activeItemRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }, 0);
    setOptionsHeight(optionsRef.current.offsetHeight);
  }, [activeItemRef.current, options]);

  return (
    <div
      style={{
        width: maxWidth || '100%',
        height:
          optionsHeight && optionsHeight < maxHeight
            ? optionsHeight
            : maxHeight,
        position: 'absolute',
        marginTop: 1,
        marginBottom: 2,
        ...(alignment === 'left'
          ? { left: 0, right: 'auto' }
          : { left: 'auto', right: 0 }),
      }}
    >
      <SelectOptions
        role="listbox"
        maxHeight={maxHeight}
        selectVariant={selectVariant}
        borderVariant={borderVariant}
        data-testid="select-options"
        ref={optionsRef}
      >
        <div ref={inViewRefTop} />
        {options.map((option, index) => {
          const isSelected = selectionIndex === index;
          return (
            <Option
              option={option}
              key={option.label + '_' + index}
              isSelected={isSelected}
              selectSize={selectSize}
              activeItemRef={activeItemRef}
              onSelect={onSelect}
            />
          );
        })}
        <div ref={inViewRefBottom} />
      </SelectOptions>
      <BoxShadow
        boxShadow={getBoxShadow(inViewTop, inViewBottom)}
        height={maxHeight}
        borderVariant={borderVariant}
      />
    </div>
  );
};

export default Options;
